import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { ArrowLeft, ArrowRight, Check, AlertCircle, TrendingUp, Shield, Target, Clock, Sparkles, Lock, Crown } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { formatErrorMessage } from '../utils/errorHandler';

const GuidedStrategyBuilder = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [symbols, setSymbols] = useState([]);
  const [isAIPreFilled, setIsAIPreFilled] = useState(false);
  
  // Plan kontrolü
  const canCreateStrategy = user?.limits?.can_create_strategy || user?.is_pro || false;

  // Plan kısıtlaması
  useEffect(() => {
    if (user && !canCreateStrategy) {
      toast.error('Strateji oluşturmak için Pro üyelik gerekli');
      navigate('/pricing');
    }
  }, [user, canCreateStrategy, navigate]);
  
  // Form data
  const [formData, setFormData] = useState({
    name: '',
    symbol: '',
    timeframe: '1h',
    strategy_type: '',
    entry_condition_type: '',
    entry_params: {},
    exit_condition_type: 'combination',
    exit_params: {},
    risk_level: 'medium',
    description: ''
  });
  
  // Preview data
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchSymbols();
  }, []);

  // Check for AI suggestion from location state
  useEffect(() => {
    if (location.state?.aiSuggestion && !isAIPreFilled) {
      const aiData = location.state.aiSuggestion;
      
      // Pre-fill form with AI suggestion
      const preFilledData = {
        name: aiData.name || 'AI Önerisi',
        symbol: aiData.symbol || 'BTCUSDT',
        timeframe: aiData.timeframe || '1h',
        strategy_type: aiData.strategy_type || 'momentum',
        entry_condition_type: aiData.entry_condition_type || 'rsi',
        entry_params: aiData.entry_params || {},
        exit_condition_type: aiData.exit_condition_type || 'combination',
        exit_params: aiData.exit_params || {},
        risk_level: aiData.risk_level || 'medium',
        description: aiData.description || aiData.explanation || ''
      };
      
      setFormData(preFilledData);
      setIsAIPreFilled(true);
      
      toast.success('🤖 AI önerisi yüklendi! Lütfen gözden geçirin ve adım adım ilerleyin.', {
        duration: 5000
      });
    }
  }, [location.state, isAIPreFilled]);

  const fetchSymbols = async () => {
    try {
      const response = await axios.get('/market/symbols');
      setSymbols(response.data);
    } catch (error) {
      console.error('Symbols fetch error:', error);
    }
  };

  const totalSteps = 6;
  const progress = (currentStep / totalSteps) * 100;

  const updateFormData = (updates) => {
    setFormData(prev => ({ ...prev, ...updates }));
  };

  const handleNext = () => {
    // Validation for each step
    if (currentStep === 1 && !formData.name) {
      toast.error('Lütfen strateji adı girin');
      return;
    }
    if (currentStep === 2 && !formData.strategy_type) {
      toast.error('Lütfen bir strateji türü seçin');
      return;
    }
    if (currentStep === 3 && !formData.entry_condition_type) {
      toast.error('Lütfen AL koşulu seçin');
      return;
    }
    if (currentStep === 4 && !formData.exit_params.profit_target) {
      toast.error('Lütfen çıkış parametrelerini ayarlayın');
      return;
    }
    
    if (currentStep === 5) {
      // Generate preview
      generatePreview();
    }
    
    setCurrentStep(prev => Math.min(prev + 1, totalSteps));
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const generatePreview = async () => {
    setLoading(true);
    try {
      const response = await axios.post('/guided-strategies/convert', formData);
      setPreview(response.data);
    } catch (error) {
      toast.error('Önizleme oluşturulamadı');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const response = await axios.post('/guided-strategies/create', formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success('Strateji başarıyla oluşturuldu! 🎉');
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Strateji kaydedilemedi'));
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch(currentStep) {
      case 1:
        return <Step1BasicInfo formData={formData} updateFormData={updateFormData} symbols={symbols} />;
      case 2:
        return <Step2StrategyType formData={formData} updateFormData={updateFormData} />;
      case 3:
        return <Step3EntryCondition formData={formData} updateFormData={updateFormData} />;
      case 4:
        return <Step4ExitCondition formData={formData} updateFormData={updateFormData} />;
      case 5:
        return <Step5RiskManagement formData={formData} updateFormData={updateFormData} />;
      case 6:
        return <Step6Preview formData={formData} preview={preview} loading={loading} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-brand-dark py-12">
      <div className="container mx-auto px-6 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-heading font-bold mb-2 text-gradient flex items-center justify-center gap-3">
            Rehberli Strateji Oluştur
            {isAIPreFilled && (
              <Badge className="bg-gradient-to-r from-primary to-purple-600 text-white">
                <Sparkles className="h-3 w-3 mr-1" />
                AI Önerisi
              </Badge>
            )}
          </h1>
          <p className="text-slate-400">
            {isAIPreFilled 
              ? '🤖 AI stratejinizi oluşturdu. Lütfen detayları gözden geçirin ve gerekirse düzenleyin.'
              : 'Adım adım sorularla kendi trading stratejinizi oluşturun'
            }
          </p>
        </div>

        {/* Progress Bar */}
        <Card className="border-border/40 mb-6">
          <CardContent className="py-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-slate-400">Adım {currentStep} / {totalSteps}</span>
              <span className="text-sm font-medium text-primary">{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} className="h-2" />
          </CardContent>
        </Card>

        {/* Step Content */}
        <Card className="border-border/40 mb-6">
          <CardContent className="py-8">
            {renderStepContent()}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between">
          <Button
            onClick={handleBack}
            disabled={currentStep === 1}
            variant="outline"
            className="border-border/40"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Geri
          </Button>

          {currentStep < totalSteps ? (
            <Button onClick={handleNext} className="bg-primary hover:bg-primary/90">
              İleri
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          ) : (
            <Button 
              onClick={handleSave} 
              disabled={loading}
              className="bg-brand-green hover:bg-brand-green/90"
            >
              <Check className="h-4 w-4 mr-2" />
              Stratejiyi Kaydet
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// Step Components
const Step1BasicInfo = ({ formData, updateFormData, symbols }) => (
  <div className="space-y-6">
    <div className="text-center mb-6">
      <Sparkles className="h-12 w-12 mx-auto mb-3 text-primary" />
      <h2 className="text-2xl font-bold mb-2">Temel Bilgiler</h2>
      <p className="text-slate-400">Stratejinize bir isim verin ve varlık seçin</p>
    </div>

    <div>
      <Label htmlFor="name">Strateji Adı *</Label>
      <Input
        id="name"
        placeholder="Örn: Benim RSI Stratejim"
        value={formData.name}
        onChange={(e) => updateFormData({ name: e.target.value })}
        className="bg-brand-dark border-border/40 mt-2"
      />
    </div>

    <div>
      <Label htmlFor="symbol">Hangi Varlık Üzerinde Çalışsın? *</Label>
      <Select value={formData.symbol} onValueChange={(value) => updateFormData({ symbol: value })}>
        <SelectTrigger className="bg-brand-dark border-border/40 mt-2">
          <SelectValue placeholder="Varlık seçin" />
        </SelectTrigger>
        <SelectContent className="bg-brand-card border-border/40">
          {symbols.map(sym => (
            <SelectItem key={sym.symbol} value={sym.symbol}>
              {sym.name} ({sym.symbol})
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>

    <div>
      <Label htmlFor="timeframe">Zaman Dilimi *</Label>
      <Select value={formData.timeframe} onValueChange={(value) => updateFormData({ timeframe: value })}>
        <SelectTrigger className="bg-brand-dark border-border/40 mt-2">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-brand-card border-border/40">
          <SelectItem value="1h">1 Saat (Kısa Vade)</SelectItem>
          <SelectItem value="4h">4 Saat (Orta Vade)</SelectItem>
          <SelectItem value="1d">1 Gün (Uzun Vade)</SelectItem>
        </SelectContent>
      </Select>
    </div>
  </div>
);

const Step2StrategyType = ({ formData, updateFormData }) => {
  const strategyTypes = [
    {
      value: 'momentum',
      title: 'Momentum (Trend Takip)',
      description: 'Güçlü trendleri takip eder. Trend yönünde işlem yapar.',
      icon: <TrendingUp className="h-8 w-8 text-blue-400" />
    },
    {
      value: 'mean_reversion',
      title: 'Mean Reversion',
      description: 'Aşırı alım/satım bölgelerinden dönüşleri yakalar.',
      icon: <Target className="h-8 w-8 text-green-400" />
    },
    {
      value: 'breakout',
      title: 'Breakout (Kırılım)',
      description: 'Fiyat seviyelerini kırdığında işlem açar. Yüksek volatilite.',
      icon: <Shield className="h-8 w-8 text-red-400" />
    }
  ];

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold mb-2">Strateji Türü</h2>
        <p className="text-slate-400">Hangi tarz strateji oluşturmak istiyorsunuz?</p>
      </div>

      <div className="grid gap-4">
        {strategyTypes.map(type => (
          <Card
            key={type.value}
            className={`cursor-pointer transition-all hover:border-primary/50 ${
              formData.strategy_type === type.value ? 'border-primary bg-primary/10' : 'border-border/40'
            }`}
            onClick={() => updateFormData({ strategy_type: type.value })}
          >
            <CardContent className="py-4">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  {type.icon}
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-lg mb-1">{type.title}</h3>
                  <p className="text-sm text-slate-400">{type.description}</p>
                </div>
                {formData.strategy_type === type.value && (
                  <Check className="h-6 w-6 text-primary flex-shrink-0" />
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

const Step3EntryCondition = ({ formData, updateFormData }) => {
  const entryConditions = [
    {
      value: 'rsi',
      title: 'RSI Tabanlı',
      description: 'RSI 30\'un altına düştüğünde AL (Aşırı satım)',
      params: { rsi_period: 14, rsi_threshold: 30, condition: 'below' }
    },
    {
      value: 'ma_crossover',
      title: 'Moving Average Çakışması',
      description: 'Hızlı MA yavaş MA\'yı yukarı kestiğinde AL',
      params: { ma_fast: 50, ma_slow: 200, crossover_type: 'golden' }
    },
    {
      value: 'price_breakout',
      title: 'Fiyat Kırılımı',
      description: 'Fiyat direnç seviyesini yukarı kırdığında AL',
      params: { resistance_level: 'auto', confirmation: true }
    }
  ];

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold mb-2">AL Koşulu</h2>
        <p className="text-slate-400">Ne zaman almak istersiniz?</p>
      </div>

      <div className="grid gap-4">
        {entryConditions.map(condition => (
          <Card
            key={condition.value}
            className={`cursor-pointer transition-all hover:border-primary/50 ${
              formData.entry_condition_type === condition.value ? 'border-primary bg-primary/10' : 'border-border/40'
            }`}
            onClick={() => updateFormData({ 
              entry_condition_type: condition.value,
              entry_params: condition.params
            })}
          >
            <CardContent className="py-4">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-bold mb-1">{condition.title}</h3>
                  <p className="text-sm text-slate-400">{condition.description}</p>
                </div>
                {formData.entry_condition_type === condition.value && (
                  <Check className="h-6 w-6 text-primary flex-shrink-0" />
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

const Step4ExitCondition = ({ formData, updateFormData }) => {
  const [profitTarget, setProfitTarget] = useState(formData.exit_params.profit_target || 10);
  const [stopLoss, setStopLoss] = useState(formData.exit_params.stop_loss || 5);

  useEffect(() => {
    updateFormData({
      exit_params: {
        ...formData.exit_params,
        profit_target: profitTarget,
        stop_loss: stopLoss
      }
    });
  }, [profitTarget, stopLoss]);

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold mb-2">ÇIKIŞ Koşulu</h2>
        <p className="text-slate-400">Ne zaman çıkmak istersiniz?</p>
      </div>

      <div className="space-y-6">
        <div>
          <Label htmlFor="profit">Kar Hedefi (%) *</Label>
          <div className="flex items-center space-x-4 mt-2">
            <Input
              id="profit"
              type="number"
              min="1"
              max="100"
              value={profitTarget}
              onChange={(e) => setProfitTarget(Number(e.target.value))}
              className="bg-brand-dark border-border/40"
            />
            <Badge variant="outline" className="text-brand-green border-brand-green">
              +{profitTarget}%
            </Badge>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Bu kar hedefine ulaşıldığında otomatik SAT sinyali üretilir
          </p>
        </div>

        <div>
          <Label htmlFor="stoploss">Stop-Loss (%) *</Label>
          <div className="flex items-center space-x-4 mt-2">
            <Input
              id="stoploss"
              type="number"
              min="1"
              max="50"
              value={stopLoss}
              onChange={(e) => setStopLoss(Number(e.target.value))}
              className="bg-brand-dark border-border/40"
            />
            <Badge variant="outline" className="text-brand-red border-brand-red">
              -{stopLoss}%
            </Badge>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Bu zarar seviyesine ulaşıldığında otomatik SAT sinyali üretilir
          </p>
        </div>

        {stopLoss >= profitTarget && (
          <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4">
            <div className="flex items-start space-x-2">
              <AlertCircle className="h-5 w-5 text-yellow-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-yellow-300">
                ⚠️ Stop-loss kar hedefinden büyük veya eşit. Bu mantıksız bir ayar olabilir.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Step5RiskManagement = ({ formData, updateFormData }) => {
  const riskLevels = [
    {
      value: 'low',
      title: 'Düşük Risk',
      description: 'Konservatif yaklaşım. Küçük kayıplar, küçük karlar.',
      color: 'green',
      icon: '🛡️',
      defaults: { stop_loss: 3, profit_target: 7 }
    },
    {
      value: 'medium',
      title: 'Orta Risk',
      description: 'Dengeli yaklaşım. Orta seviye kayıp ve kar hedefi.',
      color: 'yellow',
      icon: '⚖️',
      defaults: { stop_loss: 5, profit_target: 12 }
    },
    {
      value: 'high',
      title: 'Yüksek Risk',
      description: 'Agresif yaklaşım. Büyük kayıplar, büyük karlar.',
      color: 'red',
      icon: '🔥',
      defaults: { stop_loss: 8, profit_target: 20 }
    }
  ];

  const handleRiskSelect = (level) => {
    const selected = riskLevels.find(r => r.value === level);
    updateFormData({
      risk_level: level,
      exit_params: {
        ...formData.exit_params,
        ...selected.defaults
      }
    });
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold mb-2">Risk Yönetimi</h2>
        <p className="text-slate-400">Risk seviyenizi belirleyin</p>
      </div>

      <div className="grid gap-4">
        {riskLevels.map(level => (
          <Card
            key={level.value}
            className={`cursor-pointer transition-all hover:border-${level.color}-500/50 ${
              formData.risk_level === level.value 
                ? `border-${level.color}-500 bg-${level.color}-500/10` 
                : 'border-border/40'
            }`}
            onClick={() => handleRiskSelect(level.value)}
          >
            <CardContent className="py-4">
              <div className="flex items-start space-x-4">
                <div className="text-3xl flex-shrink-0">
                  {level.icon}
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-lg mb-1">{level.title}</h3>
                  <p className="text-sm text-slate-400 mb-2">{level.description}</p>
                  <div className="flex space-x-4 text-xs">
                    <span className="text-slate-500">
                      Stop-loss: <span className="text-brand-red">%{level.defaults.stop_loss}</span>
                    </span>
                    <span className="text-slate-500">
                      Kar hedefi: <span className="text-brand-green">%{level.defaults.profit_target}</span>
                    </span>
                  </div>
                </div>
                {formData.risk_level === level.value && (
                  <Check className="h-6 w-6 text-primary flex-shrink-0" />
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

const Step6Preview = ({ formData, preview, loading }) => {
  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin h-12 w-12 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-slate-400">Strateji oluşturuluyor...</p>
      </div>
    );
  }

  if (!preview) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-400">Önizleme yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <Check className="h-12 w-12 mx-auto mb-3 text-brand-green" />
        <h2 className="text-2xl font-bold mb-2">Strateji Hazır!</h2>
        <p className="text-slate-400">Stratejinizin özetini inceleyin</p>
      </div>

      <Card className="border-primary/40 bg-primary/5">
        <CardHeader>
          <CardTitle className="text-xl">{formData.name}</CardTitle>
          <CardDescription>{formData.symbol} | {formData.timeframe}</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-slate-300 mb-4">{preview.auto_description}</p>
          
          <div className="flex flex-wrap gap-2 mb-4">
            {preview.risk_tags?.map((tag, idx) => (
              <Badge key={idx} variant="outline" className="border-red-500/40 text-red-400">
                {tag}
              </Badge>
            ))}
            {preview.style_tags?.map((tag, idx) => (
              <Badge key={idx} variant="outline" className="border-blue-500/40 text-blue-400">
                {tag}
              </Badge>
            ))}
          </div>

          {preview.warnings && preview.warnings.length > 0 && (
            <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4">
              <h4 className="font-bold text-yellow-400 mb-2">⚠️ Uyarılar:</h4>
              {preview.warnings.map((warning, idx) => (
                <p key={idx} className="text-sm text-yellow-300 mb-1">{warning}</p>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="bg-brand-card/30 border border-border/40 rounded-lg p-4">
        <p className="text-sm text-slate-400 text-center">
          ℹ️ Bu strateji otomatik olarak oluşturuldu. Gelişmiş modda düzenlemek isterseniz, kaydettikten sonra strateji detay sayfasından "Gelişmiş Modda Düzenle" butonuna tıklayabilirsiniz.
        </p>
      </div>
    </div>
  );
};

export default GuidedStrategyBuilder;
